// OpenStreetMap Nominatim geocoding service (free, no API key required)

export interface GeocodeResult {
  latitude: number;
  longitude: number;
  display_name: string;
}

// Geocode an address to coordinates
export async function geocodeAddress(address: string, countryCode = 'GM'): Promise<GeocodeResult | null> {
  try {
    let query = address.trim();
    
    // Handle Google Plus codes (like C77W+5MW) - keep as is, they work with global search
    const isPlusCode = /^[23456789CFGHJMPQRVWX]{2,8}\+[23456789CFGHJMPQRVWX]{2,3}$/i.test(query);
    
    if (isPlusCode) {
      // For Plus codes, search globally first, then validate if in Gambia
      query = `${address}`;
    } else {
      // For regular addresses, add Gambia context
      query = `${address}, Gambia`;
    }
    
    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&${isPlusCode ? '' : `countrycodes=${countryCode}&`}limit=1`;
    
    const response = await fetch(url, {
      headers: {
        'User-Agent': 'Gamgo Food Delivery App' // Required by Nominatim
      }
    });

    if (!response.ok) {
      throw new Error(`Geocoding request failed: ${response.status}`);
    }

    const data = await response.json();

    if (data && data.length > 0) {
      const result = data[0];
      
      // For Plus codes, verify the result is in Gambia/nearby region
      if (isPlusCode) {
        const lat = parseFloat(result.lat);
        const lon = parseFloat(result.lon);
        
        // Gambia is roughly between lat 13.0-13.8 and lon -17.0 to -13.8
        // Allow some buffer for nearby locations
        if (lat < 12.5 || lat > 14.5 || lon < -17.5 || lon > -13.0) {
          console.log('Plus code location outside Gambia region:', { lat, lon });
          return null;
        }
      }
      
      return {
        latitude: parseFloat(result.lat),
        longitude: parseFloat(result.lon),
        display_name: result.display_name
      };
    }

    return null;
  } catch (error) {
    console.error('Geocoding error:', error);
    return null;
  }
}

// Reverse geocode coordinates to address
export async function reverseGeocode(latitude: number, longitude: number): Promise<string | null> {
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`;
    
    const response = await fetch(url, {
      headers: {
        'User-Agent': 'Gamgo Food Delivery App'
      }
    });

    if (!response.ok) {
      throw new Error(`Reverse geocoding request failed: ${response.status}`);
    }

    const data = await response.json();

    if (data && data.display_name) {
      return data.display_name;
    }

    return null;
  } catch (error) {
    console.error('Reverse geocoding error:', error);
    return null;
  }
}

// Cache for geocoded addresses to avoid repeated API calls
const geocodeCache = new Map<string, GeocodeResult>();

export async function geocodeAddressCached(address: string): Promise<GeocodeResult | null> {
  const cacheKey = address.toLowerCase().trim();
  
  if (geocodeCache.has(cacheKey)) {
    return geocodeCache.get(cacheKey)!;
  }

  const result = await geocodeAddress(address);
  
  if (result) {
    geocodeCache.set(cacheKey, result);
  }

  return result;
}